/**
 * Firmengeschichte: Jahresberichte, Zeitleiste, Rekorde, Verlauf.
 *
 * Alle vier Ansichten lesen denselben Datensatz – die Monatsabschlüsse aus
 * `world.history`. Sie erzeugen keine Simulation, sie deuten sie.
 *
 * Der Aufbau folgt der Frage, mit der ein Spieler herkommt. Meist ist es
 * "wie lief das letzte Jahr?", deshalb steht der Jahresbericht zuerst. Die
 * Zeitleiste beantwortet "was ist bisher passiert", die Rekorde "was war mein
 * bestes Ergebnis", der Verlauf "wohin geht die Kurve".
 */
import { useEffect, useMemo, useState } from 'react';
import type { AnnualReport, ChronicleEntry, CompanyValue, HistoryTotals, MonthlyRecord, RecordEntry } from '@hosting-sim/core';
import { calendarYear, tickToDate } from '@hosting-sim/core';
import { Badge, Card, EmptyState, PageHeader, Select, Spinner, Stat, StatGrid, Tabs, type Tone } from '../components/ui';
import { LineChart } from '../components/charts/Charts';
import { gameDay, money, num } from '../lib/format';
import { api } from '../lib/api';
import { useGame, useMetrics } from '../store/game';

const KIND_ICON: Record<ChronicleEntry['kind'], string> = {
  gruendung: '★',
  meilenstein: '◎',
  ausbau: '▣',
  forschung: '⚛',
  stoerung: '⚠',
  rekord: '⬆',
};

const KIND_TONE: Record<ChronicleEntry['kind'], Tone> = {
  gruendung: 'info',
  meilenstein: 'success',
  ausbau: 'info',
  forschung: 'neutral',
  stoerung: 'warning',
  rekord: 'success',
};

/** Formatiert einen Wert entsprechend seiner Einheit. */
function figureValue(value: number, unit: 'eur' | 'count' | 'mbps' | 'percent'): string {
  switch (unit) {
    case 'eur':
      return money(value, true);
    case 'mbps':
      return `${num(Math.round(value))} Mbit/s`;
    case 'percent':
      return `${value.toFixed(value < 10 ? 1 : 0)} %`;
    default:
      return num(Math.round(value));
  }
}

export function Chronicle() {
  const gameId = useGame((s) => s.gameId);
  const metrics = useMetrics();
  // Neu geladen wird zum Monatswechsel; häufiger entsteht nichts Neues.
  const monthIndex = Math.floor((metrics?.meta.tick ?? 0) / (24 * 30));

  const [months, setMonths] = useState<MonthlyRecord[] | null>(null);
  const [chronicle, setChronicle] = useState<ChronicleEntry[]>([]);
  const [years, setYears] = useState<number[]>([]);
  const [totals, setTotals] = useState<HistoryTotals | null>(null);
  const [value, setValue] = useState<CompanyValue | null>(null);
  const [bests, setBests] = useState<RecordEntry[]>([]);
  const [tab, setTab] = useState<'year' | 'timeline' | 'records' | 'curves'>('year');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!gameId) return;
    let cancelled = false;

    Promise.all([api.history(gameId), api.historyRecords(gameId)])
      .then(([history, recordData]) => {
        if (cancelled) return;
        setMonths(history.months);
        setChronicle(history.chronicle);
        setYears(history.years);
        setTotals(history.totals);
        setValue(history.value);
        setBests(recordData.records);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });

    return () => {
      cancelled = true;
    };
  }, [gameId, monthIndex]);

  if (error) {
    return (
      <div className="space-y-4">
        <PageHeader title="Chronik" />
        <EmptyState title="Die Firmengeschichte konnte nicht geladen werden." description={error} />
      </div>
    );
  }

  if (!months) {
    return (
      <div className="space-y-4">
        <PageHeader title="Chronik" />
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Chronik"
        description="Was aus dieser Firma geworden ist – aus den Abschlüssen jedes Spielmonats."
      />

      <StatGrid cols={4}>
        <Stat label="Geführte Monate" value={num(totals?.months ?? 0)} hint="seit Einführung der Chronik" />
        <Stat label="Umsatz gesamt" value={money(totals?.revenue ?? 0, true)} hint="Summe aller Monatsabschlüsse" />
        <Stat
          label="Ergebnis gesamt"
          value={money(totals?.profit ?? 0, true)}
          tone={(totals?.profit ?? 0) >= 0 ? 'positive' : 'negative'}
          hint={`${num(totals?.acquired ?? 0)} gewonnen, ${num(totals?.churned ?? 0)} verloren`}
        />
        <Stat
          label="Unternehmenswert"
          value={money(value?.total ?? 0, true)}
          tone={(value?.total ?? 0) >= 0 ? 'positive' : 'negative'}
          hint={
            value
              ? `Anlagen ${money(value.assets, true)} · Kunden ${money(value.customerBase, true)} · Adressen ${money(value.addresses, true)}`
              : undefined
          }
        />
      </StatGrid>

      <Tabs
        active={tab}
        onChange={(id) => setTab(id as typeof tab)}
        tabs={[
          { id: 'year', label: 'Jahresbericht' },
          { id: 'timeline', label: 'Zeitleiste', badge: chronicle.length },
          { id: 'records', label: 'Rekorde', badge: bests.length },
          { id: 'curves', label: 'Verlauf' },
        ]}
      />

      {months.length === 0 ? (
        <EmptyState
          title="Noch keine Abschlüsse"
          description="Der erste Monatsabschluss entsteht zum nächsten Monatswechsel. Ab dann füllt sich die Chronik von selbst."
        />
      ) : tab === 'year' ? (
        <YearReport gameId={gameId!} years={years} />
      ) : tab === 'timeline' ? (
        <Timeline entries={chronicle} />
      ) : tab === 'records' ? (
        <Records entries={bests} />
      ) : (
        <Curves months={months} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Jahresbericht
// ---------------------------------------------------------------------------

function YearReport({ gameId, years }: { gameId: string; years: number[] }) {
  const [year, setYear] = useState(() => years[years.length - 1] ?? 1);
  const [report, setReport] = useState<AnnualReport | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .annualReport(gameId, year)
      .then((data) => !cancelled && setReport(data))
      .catch(() => !cancelled && setReport(null));
    return () => {
      cancelled = true;
    };
  }, [gameId, year]);

  if (years.length === 0) return <EmptyState title="Noch kein abgeschlossenes Jahr" />;

  return (
    <div className="space-y-4">
      <Select label="Geschäftsjahr" value={String(year)} onChange={(e) => setYear(Number(e.target.value))} className="w-48">
        {years.map((y) => (
          <option key={y} value={y}>
            {calendarYear(y)}
          </option>
        ))}
      </Select>

      {!report ? (
        <EmptyState title={`Für ${calendarYear(year)} liegt kein Abschluss vor.`} />
      ) : (
        <>
          {/* Die Deutung steht über den Zahlen: Sie ist der Grund, warum man
              den Bericht überhaupt aufschlägt. */}
          {report.highlights.length > 0 && (
            <Card title={`Geschäftsjahr ${report.calendarYear}`}>
              <ul className="space-y-2">
                {report.highlights.map((text, index) => (
                  <li key={index} className="text-sm text-panel-700 dark:text-panel-200">
                    {text}
                  </li>
                ))}
              </ul>
            </Card>
          )}

          <Card title="Zahlen des Jahres">
            <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3 lg:grid-cols-5">
              {report.figures.map((figure) => (
                <div key={figure.label}>
                  <p className="label">{figure.label}</p>
                  <p className="tabular text-sm font-semibold">{figureValue(figure.value, figure.unit)}</p>
                  {figure.change !== null && (
                    <p
                      className={
                        'tabular text-2xs ' +
                        (figure.change >= 1 ? 'text-accent-600 dark:text-accent-400' : 'text-red-600 dark:text-red-400')
                      }
                    >
                      {figure.change >= 1 ? '▲' : '▼'} {Math.abs(Math.round((figure.change - 1) * 100))} % ggü. Vorjahr
                    </p>
                  )}
                </div>
              ))}
            </div>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card title="Kündigungen nach Ursache">
              {report.churn.length === 0 ? (
                <p className="text-xs text-panel-500 dark:text-panel-400">In diesem Jahr hat niemand gekündigt.</p>
              ) : (
                <ul className="space-y-1.5">
                  {report.churn.map((entry) => (
                    <li key={entry.reason} className="flex items-baseline justify-between gap-3 text-sm">
                      <span>{entry.label}</span>
                      <span className="tabular font-medium">{num(entry.count)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card title="Sonstiges">
              <dl className="space-y-1.5 text-sm">
                <div className="flex justify-between gap-3">
                  <dt className="text-panel-500 dark:text-panel-400">Neue Standorte</dt>
                  <dd className="tabular font-medium">{report.newDatacenters}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-panel-500 dark:text-panel-400">Längste Ausfallzeit</dt>
                  <dd className="tabular font-medium">{report.worstDowntimeTicks} h</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-panel-500 dark:text-panel-400">Forschung</dt>
                  <dd className="text-right font-medium">{report.research.length > 0 ? report.research.join(', ') : '–'}</dd>
                </div>
              </dl>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Zeitleiste
// ---------------------------------------------------------------------------

function Timeline({ entries }: { entries: ChronicleEntry[] }) {
  /** Nach Spieljahr gruppiert – so liest sich eine Firmengeschichte. */
  const byYear = useMemo(() => {
    const groups = new Map<number, ChronicleEntry[]>();
    for (const entry of entries) {
      const year = Math.floor(entry.tick / (24 * 360)) + 1;
      groups.set(year, [...(groups.get(year) ?? []), entry]);
    }
    return [...groups.entries()].sort((a, b) => a[0] - b[0]);
  }, [entries]);

  if (entries.length === 0) return <EmptyState title="Die Chronik ist noch leer." />;

  return (
    <Card>
      <div className="space-y-5">
        {byYear.map(([year, items]) => (
          <div key={year} className="flex gap-4">
            <div className="w-16 shrink-0 pt-0.5">
              <span className="tabular text-sm font-semibold">{calendarYear(year)}</span>
            </div>
            <ul className="min-w-0 flex-1 space-y-1.5 border-l divider pl-4">
              {items.map((entry, index) => (
                <li key={`${entry.tick}-${index}`} className="flex items-baseline gap-2 text-sm">
                  <Badge tone={KIND_TONE[entry.kind]}>{KIND_ICON[entry.kind]}</Badge>
                  <span className="min-w-0 flex-1">{entry.text}</span>
                  <span className="tabular shrink-0 text-2xs text-panel-400 dark:text-panel-500">
                    {gameDay(tickToDate(entry.tick))}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Rekorde
// ---------------------------------------------------------------------------

function Records({ entries }: { entries: RecordEntry[] }) {
  if (entries.length === 0) {
    return <EmptyState title="Noch keine Bestmarken" description="Rekorde entstehen aus den Monatsabschlüssen." />;
  }

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {entries.map((entry) => (
        <Card key={entry.key}>
          <p className="label">{entry.label}</p>
          <p className="tabular mt-1 text-xl font-semibold">{figureValue(entry.value, entry.unit)}</p>
          <p className="mt-1 text-2xs text-panel-500 dark:text-panel-400">
            {gameDay(tickToDate(entry.tick))} · {entry.hint}
          </p>
        </Card>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Verlauf
// ---------------------------------------------------------------------------

/**
 * Die Langzeitkurven.
 *
 * Genau das, was mit dem Ringpuffer im Monitoring nicht ging: Er reicht zehn
 * Spieltage weit, diese Kurven reichen über die gesamte Partie.
 */
function Curves({ months }: { months: MonthlyRecord[] }) {
  const series = (pick: (m: MonthlyRecord) => number) => months.map(pick);

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card title="Kunden" subtitle="Bestand am Monatsende">
        <LineChart series={[{ label: 'Kunden', points: series((m) => m.customers), color: '#21a471' }]} height={180} />
      </Card>

      <Card title="Umsatz und Ergebnis" subtitle="je Monat">
        <LineChart
          series={[
            { label: 'Umsatz', points: series((m) => m.revenue), color: '#3b82f6' },
            { label: 'Gewinn', points: series((m) => m.profit), color: '#21a471' },
          ]}
          unit="€"
          height={180}
        />
      </Card>

      <Card title="Unternehmenswert">
        <LineChart series={[{ label: 'Wert', points: series((m) => m.companyValue), color: '#f59e0b' }]} unit="€" height={180} />
      </Card>

      <Card title="Verkehr und Transitanteil">
        <LineChart
          series={[
            { label: 'Verkehr', points: series((m) => m.trafficMbps), color: '#21a471' },
            { label: 'Transit (abrechenbar)', points: series((m) => m.transitBillableMbps), color: '#f59e0b' },
          ]}
          unit="Mbit/s"
          height={180}
        />
      </Card>

      <Card title="Ruf und Zufriedenheit">
        <LineChart
          series={[
            { label: 'Ruf', points: series((m) => m.reputation), color: '#3b82f6' },
            { label: 'Zufriedenheit', points: series((m) => m.satisfaction), color: '#21a471' },
          ]}
          height={180}
        />
      </Card>

      <Card title="Ausbau" subtitle="Server, Racks, Personal">
        <LineChart
          series={[
            { label: 'Server', points: series((m) => m.servers), color: '#21a471' },
            { label: 'Racks', points: series((m) => m.racks), color: '#3b82f6' },
            { label: 'Personal', points: series((m) => m.staff), color: '#f59e0b' },
          ]}
          height={180}
        />
      </Card>
    </div>
  );
}
